import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { CookieBannerUniversal } from 'react-cookie-banner';
import Helmet from 'react-helmet';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { matchRoutes, renderRoutes } from 'react-router-config';
import { NavLink } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import normalize from 'normalize.css';
import appIcon from 'assets/app-icon.jpg';
import crossIconUrl from 'assets/cross_icon.svg';
import FONTS, { setFontFace } from 'assets/fonts';
import sharingUrl from 'assets/sharing.jpg';
import { media } from 'components/responsive';
import COLORS from 'constants/colors';
import faviconUrl from 'public/favicon.ico';
import { originSelector } from 'selectors/root';
import ErrorComponent from './ErrorComponent';
// eslint-disable-next-line import/no-unresolved
import 'assets/fonts';
import 'styles/globals.scss';
const messages = defineMessages({
    title: 'Sweatcoin: Healthier planet. Healthier, wealthier you',
    description: 'Join our 200M+ users to become part of the movement economy. Sweatcoin app converts your steps into sweatcoins — virtual currency that you can spend on products and services. It Pays to Walk.',
    cookies: 'We use cookies 🍪 to provide the best experience. By continuing to use our website, you agree to our cookies policy',
    privacy: 'Privacy policy',
});
const errorSelector = (state) => state.error.nextCode;
const MAIN_DOMAIN = 'https://sweatco.in';
const tiktokPixelCode = `
!function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
  ttq.load('CVVPS7JC77U2L7VIS6NG');
  ttq.page();
}(window, document, 'ttq');
`;
const apolloTrackerCode = `
function initApollo(){var n=Math.random().toString(36).substring(7),o=document.createElement("script");
o.src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+n,o.async=!0,o.defer=!0,
o.onload=function(){window.trackingFunctions.onLoad({appId:"663208a16f58bc01c71da9e9"})},
document.head.appendChild(o)}initApollo();
`;
const Root = (props) => {
    const { location, route, serverRouteParams } = props;
    const branch = matchRoutes([route], location.pathname);
    const { cookies = true } = branch[branch.length - 1].route;
    const intl = useIntl();
    const origin = useSelector(originSelector);
    const code = useSelector(errorSelector);
    // The banner's presence depends on the `accepts-cookies` cookie, and a shared
    // cache keys on neither it nor anything that implies it — so a visitor who had
    // accepted could fill the cache with banner-free HTML that first-time visitors
    // then get served. Rendering it only after mount keeps the SSR markup identical
    // for everyone, which is what makes those pages shareable at all.
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    // dont render cookiebar on Android Instructions page & Showroom page
    const shouldShowCookieBanner = mounted && cookies && location.pathname !== '/android-instructions' && !location.pathname.includes('showroom');
    const absoluteSharingUrl = require('url').resolve(origin, sharingUrl);
    const noindex = origin !== MAIN_DOMAIN ? [{ name: 'robots', content: 'noindex' }] : [];
    const canonical = origin === MAIN_DOMAIN
        ? [
            {
                rel: 'canonical',
                href: `${origin}${location.pathname}${location.search}`,
            },
        ]
        : [];
    const imageSrc = serverRouteParams && serverRouteParams.disableDefaultImageSrc
        ? []
        : [{ rel: 'image_src', href: absoluteSharingUrl }];
    const title = intl.formatMessage(messages.title);
    const description = intl.formatMessage(messages.description);
    return (_jsxs(_Fragment, { children: [_jsx(GlobalStyle, {}), shouldShowCookieBanner ? (_jsx(CookieBannerUniversal, { dismissOnScroll: false, cookiePath: "/", children: (onAccept) => (_jsxs(CookieBanner, { children: [_jsx(StyledNavLink, { to: "/privacy", children: _jsx(FormattedMessage, Object.assign({}, messages.cookies)) }), _jsx(CookieBannerClose, { onClick: onAccept })] })) })) : null, _jsxs("section", { className: classnames('root', 'max-height'), children: [_jsxs(Helmet, { title: title, link: [{ rel: 'shortcut icon', href: faviconUrl }, ...imageSrc, ...canonical], meta: [
                            { name: 'apple-mobile-web-app-capable', content: 'yes' },
                            { name: 'description', content: description },
                            { property: 'al:ios:url', content: 'swc://' },
                            { property: 'al:ios:app_store_id', content: '971023427' },
                            { property: 'al:ios:app_name', content: title },
                            { property: 'al:web:url', content: 'https://sweatco.in/' },
                            {
                                property: 'og:url',
                                content: `${origin}${location.pathname}${location.search}`,
                            },
                            { property: 'og:type', content: 'website' },
                            { property: 'og:site_name', content: 'Sweatcoin' },
                            { property: 'og:title', content: title },
                            { property: 'og:description', content: description },
                            { property: 'og:image', content: absoluteSharingUrl },
                            { property: 'fb:app_id', content: '185968838439659' },
                            { name: 'twitter:title', content: title },
                            { name: 'twitter:description', content: description },
                            { name: 'twitter:image', content: absoluteSharingUrl },
                            ...noindex,
                        ], children: [_jsx("script", { type: "application/ld+json", children: `{
              "@context": "https://schema.org",
              "@type": "Organization",
              "url": "https://sweatco.in",
              "logo": "${appIcon}"
            }` }), _jsx("script", { children: tiktokPixelCode }), _jsx("script", { children: apolloTrackerCode }), _jsx("body", { className: "max-height" })] }), _jsx("div", { className: "root__content max-height", children: code ? renderError(code) : renderRoutes(route.routes) })] })] }));
    function renderError(code) {
        return _jsx(ErrorComponent, { code: code });
    }
};
const GlobalStyle = createGlobalStyle `
${normalize}
${setFontFace(FONTS.SimplonNorm)}
${setFontFace(FONTS.SimplonNormBold, 'bold')}
${setFontFace(FONTS.SimplonMono)}
${setFontFace(FONTS.LucidaGrande)}
/* latin */
@font-face {
  font-family: 'Orbitron';
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/orbitron/v25/yMJRMIlzdpvBhQQL_Qq7dy1biN15.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
/* latin */
@font-face {
  font-family: 'Orbitron';
  font-style: normal;
  font-weight: 500;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/orbitron/v25/yMJRMIlzdpvBhQQL_Qq7dy1biN15.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
/* latin */
@font-face {
  font-family: 'Orbitron';
  font-style: normal;
  font-weight: 600;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/orbitron/v25/yMJRMIlzdpvBhQQL_Qq7dy1biN15.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
/* latin */
@font-face {
  font-family: 'Orbitron';
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/orbitron/v25/yMJRMIlzdpvBhQQL_Qq7dy1biN15.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
/* latin */
@font-face {
  font-family: 'Orbitron';
  font-style: normal;
  font-weight: 800;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/orbitron/v25/yMJRMIlzdpvBhQQL_Qq7dy1biN15.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
/* latin */
@font-face {
  font-family: 'Orbitron';
  font-style: normal;
  font-weight: 900;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/orbitron/v25/yMJRMIlzdpvBhQQL_Qq7dy1biN15.woff2) format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}

html {
  width: 100%;
  height: 100%;
}

html, body {
  margin: 0px;
  padding: 0px;
  font-weight: normal;
  -webkit-font-smoothing: antialiased;
  color: ${COLORS.blue};
  
  ${media.phone_medium `font-size: 22px !important;`};
  ${media.tablet `font-size: 30px !important;`};
  ${media.desktop `font-size: 26px !important;`};
  ${media.desktop_large `font-size: 34px !important;`};
}
`;
const StyledNavLink = styled(NavLink) ``;
const CookieBanner = styled.div `
  font-family: ${FONTS.SimplonNorm.name}, ${FONTS.fallback} !important;
  position: fixed;
  z-index: 999;
  font-size: 1rem;
  text-align: center;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  font-size: 0.5rem;
  padding: 0.5rem;

  ${StyledNavLink}:hover {
    text-decoration: underline;
  }
`;
const CookieBannerClose = styled.span `
  display: inline-block;
  vertical-align: middle;
  width: 0.5rem;
  height: 0.5rem;
  margin-left: 0.5rem;
  margin-top: -1px;
  background: url(${crossIconUrl}) no-repeat;
  background-size: contain;
`;
export default Root;
